import { useEffect, useState } from 'react';
import { generationLimitsApi, GenerationLimits, GenAction } from '../../lib/generationLimitsApi';
import { useDarkMode } from '../../context/DarkModeContext';
import { Save, RotateCcw, Loader2 } from 'lucide-react';
import Alert from './Alert';

const ACTIONS: { key: GenAction; label: string; description: string }[] = [
  { key: 'suggest',  label: 'Vorschläge',  description: 'Stichpunkte vorschlagen (→ „Vorschläge"-Button)' },
  { key: 'generate', label: 'Generieren',  description: 'Abschnitt neu schreiben (→ „Neu generieren"-Button im Editor)' },
  { key: 'refine',   label: 'Verfeinern',  description: 'Bestehenden Text verbessern (→ „Verfeinern"-Button)' },
];

const CARD_LABELS: Record<string, string> = {
  'card-1-1':   '1.1 Beratungsauftrag und Anlass',
  'card-1-2':   '1.2 Angaben zum Unternehmen',
  'card-1-3':   '1.3 Unternehmensdaten',
  'card-1-4':   '1.4 Geschäftsgegenstand',
  'card-1-5':   '1.5 Zeitangaben',
  'analysis':   '2. Analyse der Unternehmenssituation',
  'card-2-1':   '2.1 Ist-Zustand',
  'card-2-2':   '2.2 Stärken und Potenziale',
  'card-2-3':   '2.3 Schwächen und Handlungsbedarfe',
  'execution':  '3. Auftragsdurchführung',
  'weaknesses': '4. Schwachstellen und Ursachen',
  'card-4-1':   '4.1 Ermittelte Schwachstellen',
  'card-4-2':   '4.2 Fragestellungen zur Strukturierung',
  'action-plan':'5. Maßnahmeplan',
};

const NO_AI_CARDS = new Set(['card-1-2', 'card-1-3', 'card-1-5', 'analysis', 'weaknesses']);
const ALL_CARD_IDS = Object.keys(CARD_LABELS);

export function GenerationLimitsTab() {
  const { darkMode } = useDarkMode();
  const [data, setData] = useState<GenerationLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [defaultDraft, setDefaultDraft] = useState<Record<GenAction, string>>({ generate: '', refine: '', suggest: '' });
  const [overrideDraft, setOverrideDraft] = useState<Record<string, string>>({});

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await generationLimitsApi.list();
      setData(d);
      setDefaultDraft({
        generate: d.defaults.generate?.toString() ?? '',
        refine:   d.defaults.refine?.toString()   ?? '',
        suggest:  d.defaults.suggest?.toString()  ?? '',
      });
      const od: Record<string, string> = {};
      for (const ov of d.overrides) od[`${ov.cardId}|${ov.action}`] = ov.maxPerReport?.toString() ?? '';
      setOverrideDraft(od);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const parseValue = (s: string): number | null => {
    if (s.trim() === '') return null;
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  };

  const saveDefault = async (action: GenAction) => {
    setSaving(`default|${action}`);
    try { await generationLimitsApi.setDefault(action, parseValue(defaultDraft[action])); await reload(); }
    finally { setSaving(null); }
  };

  const saveOverride = async (cardId: string, action: GenAction) => {
    const key = `${cardId}|${action}`;
    setSaving(key);
    try { await generationLimitsApi.setCardOverride(cardId, action, parseValue(overrideDraft[key] ?? '')); await reload(); }
    finally { setSaving(null); }
  };

  const resetOverride = async (cardId: string, action: GenAction) => {
    const key = `${cardId}|${action}`;
    setSaving(key);
    try { await generationLimitsApi.resetCardOverride(cardId, action); await reload(); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (error) return <Alert type="error">Fehler: {error}</Alert>;
  if (!data) return null;

  const cardCls = `p-4 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const inputCls = `w-24 px-2 py-1 text-sm rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`;
  const btnSave = (disabled: boolean, onClick: () => void) => (
    <button onClick={onClick} disabled={disabled}
      className="px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center gap-1">
      <Save className="w-3 h-3" />Speichern
    </button>
  );

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h3 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Standard-Limits (max. Versuche pro Bericht)</h3>
        <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Leer = unbegrenzt. Gilt für alle Abschnitte ohne eigene Einstellung.</p>
        <div className="flex gap-6 flex-wrap">
          {ACTIONS.map(a => (
            <div key={a.key} className="flex items-end gap-2">
              <div>
                <label className={`block text-xs mb-0.5 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{a.label}</label>
                <span className={`block text-xs mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{a.description.split('(')[0].trim()}</span>
                <input type="number" min={0} placeholder="∞" className={inputCls}
                  value={defaultDraft[a.key]}
                  onChange={e => setDefaultDraft(p => ({ ...p, [a.key]: e.target.value }))} />
              </div>
              {btnSave(saving === `default|${a.key}`, () => saveDefault(a.key))}
            </div>
          ))}
        </div>
      </div>

      <div className={cardCls}>
        <h3 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Abschnittsspezifische Limits</h3>
        <p className={`text-sm mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Leer = Standard greift. Gilt nur für diesen Abschnitt.</p>
        <div className={`flex flex-wrap gap-4 mb-4 text-xs p-3 rounded-md ${darkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-slate-50 text-slate-500'}`}>
          {ACTIONS.map(a => (
            <span key={a.key}><span className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{a.label}:</span> {a.description}</span>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <th className="pb-2 pr-4">Abschnitt</th>
                {ACTIONS.map(a => <th key={a.key} className="pb-2 pr-2" title={a.description}>{a.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {ALL_CARD_IDS.map(cardId => {
                const noAi = NO_AI_CARDS.has(cardId);
                return (
                  <tr key={cardId} className={`border-t ${darkMode ? 'border-slate-700' : 'border-slate-100'} ${noAi ? 'opacity-40' : ''}`}>
                    <td className={`py-2 pr-4 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {CARD_LABELS[cardId] ?? cardId}
                      {noAi && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-400'}`}>Kein KI</span>}
                    </td>
                    {ACTIONS.map(a => {
                      const key = `${cardId}|${a.key}`;
                      const hasOverride = data.overrides.some(o => o.cardId === cardId && o.action === a.key);
                      const defVal = data.defaults[a.key];
                      return (
                        <td key={a.key} className="py-2 pr-2">
                          <div className="flex items-center gap-1">
                            {noAi
                              ? <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-300'}`}>—</span>
                              : <>
                                  <input type="number" min={0} placeholder={defVal !== null ? String(defVal) : '∞'}
                                    className={`${inputCls} w-20`}
                                    value={overrideDraft[key] ?? ''}
                                    onChange={e => setOverrideDraft(p => ({ ...p, [key]: e.target.value }))} />
                                  <button onClick={() => saveOverride(cardId, a.key)} disabled={saving === key}
                                    className="p-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" title="Limit speichern">
                                    <Save className="w-3 h-3" />
                                  </button>
                                  {hasOverride && (
                                    <button onClick={() => resetOverride(cardId, a.key)} disabled={saving === key}
                                      className={`p-1 rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'} disabled:opacity-50`}
                                      title="Zurück auf Standard">
                                      <RotateCcw className="w-3 h-3" />
                                    </button>
                                  )}
                                </>
                            }
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
